/**
 * Protected Route Component
 *
 * Wrapper component for routes that require authentication.
 * Redirects to login page if user is not authenticated.
 * Shows access denied page if privileges are required but not present.
 * T166: Show access denied page instead of redirecting for insufficient permissions
 *
 * Multi-tenancy (v0.13.0): "admin" is now two separate things, so the single `requireAdmin`
 * prop has been REPLACED rather than supplemented:
 *
 *   requireTenantAdmin    — authority inside the caller's own tenant (Owner or Admin)
 *   requirePlatformAdmin  — Akhile's back office (audit, email config, auth policy, tenants)
 *   requireAnyAdmin       — either of the above
 *
 * Replacing rather than adding is deliberate. Leaving `requireAdmin` in place would let a
 * route keep the old, ambiguous semantics silently, and the four back-office routes are
 * exactly the ones where getting it wrong hands a client another tenant's audit trail.
 *
 * `requireAnyAdmin` is NOT that old ambiguity returning. It exists for one route — the
 * /admin/settings hub, which is a menu of links whose destinations each keep their own guard.
 * Both tiers have somewhere to go from it, and the hub itself discloses nothing.
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Card from './Card';
import Button from './Button';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Owner or Admin of the active tenant. */
  requireTenantAdmin?: boolean;
  /** Akhile staff only — never a client, however senior in their own tenant. */
  requirePlatformAdmin?: boolean;
  /** Either tier. For the settings hub only — see the note at the top of this file. */
  requireAnyAdmin?: boolean;
}

const AccessDenied: React.FC<{ message: string; action?: ReactNode }> = ({ message, action }) => (
  <div className="container mx-auto px-4 py-8">
    <Card>
      <Card.Body>
        <div className="text-center py-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="flex justify-center gap-3">
            <Button variant="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
            {action}
          </div>
        </div>
      </Card.Body>
    </Card>
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireTenantAdmin = false,
  requirePlatformAdmin = false,
  requireAnyAdmin = false
}) => {
  const { isAuthenticated, isTenantAdmin, isPlatformAdmin, hasNoActiveTenant, logout } = useAuth();
  const location = useLocation();

  // Not authenticated - redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but with no tenant to act in. Every domain endpoint answers 400 in this
  // state, so say so plainly — otherwise the app renders empty lists that read as
  // "you have no projects" rather than "you have no workspace".
  //
  // The exemption is keyed on being a platform admin, not on the route's flag. Akhile's back
  // office must stay reachable so this can be fixed, and a staffer between client engagements
  // legitimately has no active tenant — gating on `requirePlatformAdmin` instead would lock
  // them out of the settings hub, which carries no such flag.
  //
  // This is the one gate that can refuse every route at once, so `hasNoActiveTenant` only
  // fires on an explicit null from the server — see the note on the predicate itself. Signing
  // out is offered because "sign in again" is the advice and Go Back cannot give it.
  if (hasNoActiveTenant && !isPlatformAdmin) {
    return (
      <AccessDenied
        message={
          'Your account is not currently a member of any workspace, so there is nothing to ' +
          'show. Ask an administrator to add you to one, then sign in again.'
        }
        action={
          <Button variant="outline" onClick={logout}>
            Sign out
          </Button>
        }
      />
    );
  }

  // T166: Authenticated but lacking the required privilege - show access denied page
  if (requirePlatformAdmin && !isPlatformAdmin) {
    return (
      <AccessDenied message="This page is restricted to platform administrators." />
    );
  }

  if (requireTenantAdmin && !isTenantAdmin) {
    return (
      <AccessDenied
        message={
          'You do not have permission to access this page. Workspace administrator ' +
          'privileges are required.'
        }
      />
    );
  }

  if (requireAnyAdmin && !isTenantAdmin && !isPlatformAdmin) {
    return (
      <AccessDenied
        message={
          'You do not have permission to access this page. Administrator privileges are ' +
          'required.'
        }
      />
    );
  }

  // Authenticated with required permissions - render children
  return <>{children}</>;
};
